// components/AlertSettingsDialog.jsx
import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormControlLabel,
  Switch,
  TextField,
  Grid
} from '@mui/material';
import axios from 'axios';
import { API_BASE_URL } from '../../../config/config';

const AlertSettingsDialog = ({ open, onClose, alertSettings: initialSettings, controllerId, onUpdated }) => {
  // Local state holds the alert settings data that will be modified by the form.
  const [settings, setSettings] = useState(initialSettings || {
    smsEnabled: false,
    whatsappEnabled: false,
    motorOnAlert: false,
    motorOffAlert: false,
    errorAlert: false,
    errorAlerts: {
      noError: false,
      commRxError: false,
      timeoutError: false,
      rfModuleError: false,
      timerIcError: false,
      flowError: false,
      signalError: false,
      powerFailure: false,
    },
    recipients: [],
  });

  // Update local state when initialSettings changes
  useEffect(() => {
    if (initialSettings) {
      setSettings(initialSettings);
    }
  }, [initialSettings]);

  // Generic toggle handler for top‑level fields
  const handleToggle = (field) => (event) => {
    setSettings((prev) => ({
      ...prev,
      [field]: event.target.checked,
    }));
  };

  // Toggle for error-specific fields
  const handleErrorToggle = (errorField) => (event) => {
    setSettings((prev) => ({
      ...prev,
      errorAlerts: {
        ...prev.errorAlerts,
        [errorField]: event.target.checked,
      },
    }));
  };

  // Handler for updating recipients (comma‐separated)
  const handleRecipientsChange = (event) => {
    const value = event.target.value;
    const recipientsArray = value.split(',').map((n) => n.trim()).filter((n) => n);
    setSettings((prev) => ({
      ...prev,
      recipients: recipientsArray,
    }));
  };

  // Save handler which calls the update API
  const handleSave = async () => {
    try {
      const payload = {
        smsEnabled: settings.smsEnabled,
        whatsappEnabled: settings.whatsappEnabled,
        motorOnAlert: settings.motorOnAlert,
        motorOffAlert: settings.motorOffAlert,
        errorAlert: settings.errorAlert,
        errorAlerts: settings.errorAlerts,
        recipients: settings.recipients,
      };
      // Assuming your API endpoint is: PATCH /api/controller/:id/alerts
      const response = await axios.patch(`${API_BASE_URL}api/sensor/${controllerId}/alerts`, payload);
      onUpdated && onUpdated(response.data.alertSettings);
      onClose();
    } catch (error) {
      console.error('Error updating alert settings:', error);
      // You might want to add error feedback here
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>Alert Settings</DialogTitle>
      <DialogContent dividers>
        <Grid container spacing={2}>
          <Grid item xs={6}>
            <FormControlLabel
              control={<Switch checked={settings.smsEnabled} onChange={handleToggle('smsEnabled')} color="primary" />}
              label="SMS Enabled"
            />
          </Grid>
          <Grid item xs={6}>
            <FormControlLabel
              control={<Switch checked={settings.whatsappEnabled} onChange={handleToggle('whatsappEnabled')} color="primary" />}
              label="WhatsApp Enabled"
            />
          </Grid>
          <Grid item xs={6}>
            <FormControlLabel
              control={<Switch checked={settings.motorOnAlert} onChange={handleToggle('motorOnAlert')} color="primary" />}
              label="Motor ON Alert"
            />
          </Grid>
          <Grid item xs={6}>
            <FormControlLabel
              control={<Switch checked={settings.motorOffAlert} onChange={handleToggle('motorOffAlert')} color="primary" />}
              label="Motor OFF Alert"
            />
          </Grid>
          <Grid item xs={12}>
            <FormControlLabel
              control={<Switch checked={settings.errorAlert} onChange={handleToggle('errorAlert')} color="primary" />}
              label="Global Error Alert"
            />
          </Grid>
          {settings.errorAlert && (
            <>
              <Grid item xs={6}>
                <FormControlLabel
                  control={<Switch checked={settings.errorAlerts.commRxError} onChange={handleErrorToggle('commRxError')} color="primary" />}
                  label="Comm Rx Error"
                />
              </Grid>
              <Grid item xs={6}>
                <FormControlLabel
                  control={<Switch checked={settings.errorAlerts.timeoutError} onChange={handleErrorToggle('timeoutError')} color="primary" />}
                  label="Timeout Error"
                />
              </Grid>
              <Grid item xs={6}>
                <FormControlLabel
                  control={<Switch checked={settings.errorAlerts.rfModuleError} onChange={handleErrorToggle('rfModuleError')} color="primary" />}
                  label="RF Module Error"
                />
              </Grid>
              <Grid item xs={6}>
                <FormControlLabel
                  control={<Switch checked={settings.errorAlerts.timerIcError} onChange={handleErrorToggle('timerIcError')} color="primary" />}
                  label="Timer IC Error"
                />
              </Grid>
              <Grid item xs={6}>
                <FormControlLabel
                  control={<Switch checked={settings.errorAlerts.flowError} onChange={handleErrorToggle('flowError')} color="primary" />}
                  label="Flow Error"
                />
              </Grid>
              <Grid item xs={6}>
                <FormControlLabel
                  control={<Switch checked={settings.errorAlerts.signalError} onChange={handleErrorToggle('signalError')} color="primary" />}
                  label="Signal Error"
                />
              </Grid>
              <Grid item xs={6}>
                <FormControlLabel
                  control={<Switch checked={settings.errorAlerts.powerFailure} onChange={handleErrorToggle('powerFailure')} color="primary" />}
                  label="Power Failure"
                />
              </Grid>
            </>
          )}
          <Grid item xs={12}>
            <TextField
              label="Recipients (comma-separated)"
              fullWidth
              variant="outlined"
              defaultValue={settings.recipients.join(', ')}
              onBlur={handleRecipientsChange}
              helperText="Enter phone numbers separated by commas"
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} variant="outlined">Cancel</Button>
        <Button onClick={handleSave} color="primary" variant="contained">Save</Button>
      </DialogActions>
    </Dialog>
  );
};

export default AlertSettingsDialog;
